/**
 * SiteSettingsModal
 *
 * Centered "Site settings" modal matching the May 2026 Figma. Uses the same
 * `.os-*` class palette as the Organization settings modal (see SystemPage),
 * so the two share styling.
 *
 * Tabs (left rail):
 *   1. Site details         — siteName*, timezone*, language*, description
 *   2. Contact information  — country*, state, city*, address1/2, postal,
 *                             phone* (code+number), reply-to email
 *   3. Integration          — operatorUrl
 *
 * Validation:
 *   - Tabs with at least one missing required field show a red ⓘ icon.
 *   - On Save, the modal auto-jumps to the first invalid tab.
 *   - Invalid fields show a red border + "This field is required" message.
 *
 * Backend note:
 *   Only a subset of these fields is persisted today (siteName, country,
 *   state, city, addressLine1, timezone, regionName, zipCode, description).
 *   The extras (language, addressLine2, primaryContactPhone[Code], replyTo
 *   Email, operatorUrl) are kept in form state and would need matching
 *   columns + DTO fields on the backend. Submission currently strips them.
 */
import { useEffect, useMemo, useState } from 'react';
import { Country, State } from 'country-state-city';
import organizationService from '../../services/organizationService';

const ALLOWED_COUNTRY_CODES = ['US', 'IN', 'AU', 'CN', 'CA', 'AE', 'ZA', 'GB'];
const ALL_COUNTRIES = Country.getAllCountries()
  .filter((c) => ALLOWED_COUNTRY_CODES.includes(c.isoCode))
  .map((c) => ({ code: c.isoCode, name: c.name }));

const PHONE_CODES = [
  { code: 'US', dial: '+1',   flag: '🇺🇸' },
  { code: 'CA', dial: '+1',   flag: '🇨🇦' },
  { code: 'GB', dial: '+44',  flag: '🇬🇧' },
  { code: 'IN', dial: '+91',  flag: '🇮🇳' },
  { code: 'AU', dial: '+61',  flag: '🇦🇺' },
  { code: 'CN', dial: '+86',  flag: '🇨🇳' },
  { code: 'AE', dial: '+971', flag: '🇦🇪' },
  { code: 'ZA', dial: '+27',  flag: '🇿🇦' },
];

const LANGUAGES = [
  { code: 'en',    name: 'English' },
  { code: 'es',    name: 'Spanish' },
  { code: 'fr',    name: 'French' },
  { code: 'de',    name: 'German' },
  { code: 'pt',    name: 'Portuguese' },
  { code: 'zh',    name: 'Chinese' },
  { code: 'hi',    name: 'Hindi' },
  { code: 'ar',    name: 'Arabic' },
  { code: 'ja',    name: 'Japanese' },
];

const TABS = [
  { id: 'site',        label: 'Site details',         icon: 'fa-building' },
  { id: 'contact',     label: 'Contact information',  icon: 'fa-id-card' },
  { id: 'integration', label: 'Integration',          icon: 'fa-puzzle-piece' },
];

const EMPTY = {
  siteName: '',
  timezone: '',
  language: '',
  description: '',

  country: '',
  state: '',
  city: '',
  addressLine1: '',
  addressLine2: '',
  zipCode: '',
  phoneCode: '+1',
  phoneNumber: '',
  replyToEmail: '',

  operatorUrl: '',
};

/** Required-field map per tab. Used both for inline validation and for the
 *  red ⓘ icon on the tab in the side rail. */
const REQUIRED_BY_TAB = {
  site:        ['siteName', 'timezone', 'language'],
  contact:     ['country', 'city', 'phoneNumber'],
  integration: [],
};

function isMissing(form, field) {
  return !String(form[field] ?? '').trim();
}

function tabHasError(form, tabId) {
  return REQUIRED_BY_TAB[tabId].some((f) => isMissing(form, f));
}

/** Help icon (ⓘ) next to a label. */
function Help({ tip }) {
  return (
    <span className="os-help" title={tip} aria-label={tip}>
      <i className="far fa-question-circle"></i>
    </span>
  );
}

/** Required-field label "Foo* ⓘ". */
function FieldLabel({ children, required, tip }) {
  return (
    <label className="os-label">
      <span>{children}{required && <span className="os-req">*</span>}</span>
      {tip && <Help tip={tip} />}
    </label>
  );
}

/** "This field is required" hint shown below an invalid input. */
function RequiredHint({ show }) {
  if (!show) return null;
  return (
    <div className="os-field-error">
      <i className="fas fa-info-circle"></i> This field is required
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal                                                                     */
/* -------------------------------------------------------------------------- */

export default function SiteSettingsModal({
  open,
  initial,
  onSave,
  onClose,
  saving = false,
}) {
  const [form, setForm]               = useState(EMPTY);
  const [active, setActive]           = useState('site');
  const [showErrors, setShowErrors]   = useState(false);
  const [timezones, setTimezones]     = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(false);

  const isEdit = !!initial;

  /* Reset on open. */
  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    setActive('site');
    setShowErrors(false);
  }, [open, initial]);

  /* Load the canonical IANA values while showing the portal-supplied label. */
  useEffect(() => {
    if (!open) return undefined;
    let activeRequest = true;
    setLoadingTimezones(true);
    organizationService.listTimezones()
      .then((response) => {
        if (activeRequest) setTimezones(Array.isArray(response) ? response : []);
      })
      .catch(() => {
        if (activeRequest) setTimezones([]);
      })
      .finally(() => {
        if (activeRequest) setLoadingTimezones(false);
      });
    return () => { activeRequest = false; };
  }, [open]);

  /* Esc to close. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const setField = (field) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setForm((f) => {
      if (field === 'country') return { ...f, country: val, state: '' };
      return { ...f, [field]: val };
    });
  };

  const states = useMemo(() => {
    if (!form.country) return [];
    return State.getStatesOfCountry(form.country).map((s) => ({ code: s.isoCode, name: s.name }));
  }, [form.country]);

  if (!open) return null;

  const handleSave = () => {
    setShowErrors(true);
    // Find first tab with missing required fields.
    const firstBad = TABS.find((t) => tabHasError(form, t.id));
    if (firstBad) {
      setActive(firstBad.id);
      return;
    }
    // Build payload aligned with backend SiteCreate/UpdateRequest. Extras are
    // currently dropped — wire them on the backend to persist.
    const payload = {
      siteName:     form.siteName.trim(),
      country:      form.country,
      state:        form.state || null,
      city:         form.city || null,
      addressLine1: form.addressLine1 || null,
      timezone:     form.timezone || null,
      regionName:   form.regionName || null,
      zipCode:      form.zipCode || null,
      description:  form.description || null,
    };
    onSave(payload, form);
  };

  /* -------------------------------------------------------------------- */
  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Site settings"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="os-modal os-modal-md" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>Site settings</h2>
        </header>

        <div className="os-modal-body">
          {/* Left rail tabs */}
          <aside className="os-side">
            {TABS.map((t) => {
              const hasErr = showErrors && tabHasError(form, t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`os-side-item ${active === t.id ? 'active' : ''}`}
                  onClick={() => setActive(t.id)}
                >
                  <i className={`fas ${t.icon}`}></i>
                  <span>{t.label}</span>
                  {hasErr && (
                    <span
                      className="os-side-warn"
                      data-tip="Complete all required fields in this section to continue."
                      aria-label="Section has errors"
                    >
                      <i className="fas fa-info-circle"></i>
                    </span>
                  )}
                </button>
              );
            })}
          </aside>

          {/* Right pane */}
          <section className="os-pane">
            {active === 'site' && (
              <SiteDetailsPane
                form={form}
                setField={setField}
                showErrors={showErrors}
                timezones={timezones}
                loadingTimezones={loadingTimezones}
              />
            )}
            {active === 'contact' && (
              <ContactInfoPane
                form={form}
                setField={setField}
                showErrors={showErrors}
                states={states}
              />
            )}
            {active === 'integration' && (
              <IntegrationPane form={form} setField={setField} />
            )}
          </section>
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Panes                                                                     */
/* -------------------------------------------------------------------------- */

function SearchableTimezoneSelect({ value, onChange, timezones, loading, hasError }) {
  const selectedTimezone = timezones.find((timezone) => timezone.timezoneIana === value);
  const [search, setSearch] = useState(selectedTimezone?.portalLabel || value || '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSearch(selectedTimezone?.portalLabel || value || '');
  }, [selectedTimezone, value]);

  const matchingTimezones = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return timezones;
    return timezones.filter((timezone) => (
      timezone.portalLabel.toLowerCase().includes(normalizedSearch)
      || timezone.timezoneIana.toLowerCase().includes(normalizedSearch)
    ));
  }, [search, timezones]);

  const selectTimezone = (timezone) => {
    onChange(timezone.timezoneIana);
    setSearch(timezone.portalLabel);
    setOpen(false);
  };

  return (
    <div className="os-timezone-combobox">
      <input
        type="text"
        className={`os-input ${hasError ? 'os-input-error' : ''}`}
        value={search}
        placeholder={loading ? 'Loading time zones…' : 'Search time zones'}
        disabled={loading}
        role="combobox"
        aria-expanded={open}
        aria-controls="site-timezone-options"
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(event) => {
          setSearch(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'Enter' && matchingTimezones.length === 1) {
            event.preventDefault();
            selectTimezone(matchingTimezones[0]);
          }
        }}
      />
      {open && !loading && (
        <div id="site-timezone-options" className="os-timezone-options" role="listbox">
          {matchingTimezones.length === 0 ? (
            <div className="os-timezone-empty">No matching time zones</div>
          ) : (
            matchingTimezones.map((timezone) => (
              <button
                key={timezone.id}
                type="button"
                className={`os-timezone-option ${timezone.timezoneIana === value ? 'is-selected' : ''}`}
                role="option"
                aria-selected={timezone.timezoneIana === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectTimezone(timezone)}
              >
                {timezone.portalLabel}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SiteDetailsPane({ form, setField, showErrors, timezones, loadingTimezones }) {
  const errName  = showErrors && isMissing(form, 'siteName');
  const errTz    = showErrors && isMissing(form, 'timezone');
  const errLang  = showErrors && isMissing(form, 'language');
  return (
    <>
      <div className="os-field">
        <FieldLabel required tip="The display name for this site">Site name</FieldLabel>
        <input
          type="text"
          className={`os-input ${errName ? 'os-input-error' : ''}`}
          placeholder="Site name"
          value={form.siteName}
          onChange={setField('siteName')}
        />
        <RequiredHint show={errName} />
      </div>

      <div className="os-field">
        <FieldLabel required tip="Site time zone">Time zone</FieldLabel>
        <SearchableTimezoneSelect
          value={form.timezone}
          onChange={setField('timezone')}
          timezones={timezones}
          loading={loadingTimezones}
          hasError={errTz}
        />
        <RequiredHint show={errTz} />
      </div>

      <div className="os-field">
        <FieldLabel required tip="Default language for this site">Language</FieldLabel>
        <select
          className={`os-input os-select ${errLang ? 'os-input-error' : ''}`}
          value={form.language}
          onChange={setField('language')}
        >
          <option value="">Language</option>
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <RequiredHint show={errLang} />
      </div>

      <div className="os-field">
        <FieldLabel>Description</FieldLabel>
        <textarea
          className="os-input os-textarea"
          placeholder="Optional"
          value={form.description}
          onChange={setField('description')}
          rows={4}
        />
      </div>
    </>
  );
}

function ContactInfoPane({ form, setField, showErrors, states }) {
  const errCountry = showErrors && isMissing(form, 'country');
  const errCity    = showErrors && isMissing(form, 'city');
  const errPhone   = showErrors && isMissing(form, 'phoneNumber');
  return (
    <>
      <div className="os-field">
        <FieldLabel required tip="Country where the site is located">Country</FieldLabel>
        <select
          className={`os-input os-select ${errCountry ? 'os-input-error' : ''}`}
          value={form.country}
          onChange={setField('country')}
        >
          <option value="">Country</option>
          {ALL_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <RequiredHint show={errCountry} />
      </div>

      <div className="os-field">
        <FieldLabel>State</FieldLabel>
        {states.length > 0 ? (
          <select
            className="os-input os-select"
            value={form.state}
            onChange={setField('state')}
          >
            <option value="">Select a state</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        ) : (
          <input
            type="text"
            className="os-input"
            placeholder="Select a state"
            value={form.state}
            onChange={setField('state')}
          />
        )}
      </div>

      <div className="os-field">
        <FieldLabel required tip="City">City</FieldLabel>
        <input
          type="text"
          className={`os-input ${errCity ? 'os-input-error' : ''}`}
          placeholder="City name"
          value={form.city}
          onChange={setField('city')}
        />
        <RequiredHint show={errCity} />
      </div>

      <div className="os-field">
        <FieldLabel>Address Line 1</FieldLabel>
        <input
          type="text"
          className="os-input"
          placeholder="Address"
          value={form.addressLine1}
          onChange={setField('addressLine1')}
        />
      </div>

      <div className="os-field">
        <FieldLabel>Address Line 2</FieldLabel>
        <input
          type="text"
          className="os-input"
          placeholder="Address"
          value={form.addressLine2}
          onChange={setField('addressLine2')}
        />
      </div>

      <div className="os-field">
        <FieldLabel>Postal code</FieldLabel>
        <input
          type="text"
          className="os-input"
          placeholder="Postal code"
          value={form.zipCode}
          onChange={setField('zipCode')}
        />
      </div>

      <div className="os-field">
        <FieldLabel required>Phone number</FieldLabel>
        <div className="os-phone-row">
          <select
            className="os-input os-select os-phone-code"
            value={form.phoneCode}
            onChange={setField('phoneCode')}
          >
            {PHONE_CODES.map((p) => (
              <option key={p.code} value={p.dial}>{p.flag} {p.dial}</option>
            ))}
          </select>
          <input
            type="tel"
            className={`os-input os-phone-number ${errPhone ? 'os-input-error' : ''}`}
            placeholder="eg. 1111111111"
            value={form.phoneNumber}
            onChange={setField('phoneNumber')}
          />
        </div>
        <RequiredHint show={errPhone} />
      </div>

      <div className="os-field">
        <FieldLabel>Reply-to email</FieldLabel>
        <input
          type="email"
          className="os-input"
          placeholder="Email"
          value={form.replyToEmail}
          onChange={setField('replyToEmail')}
        />
      </div>
    </>
  );
}

function IntegrationPane({ form, setField }) {
  return (
    <div className="os-field">
      <FieldLabel tip="External operator console URL for this site">Operator URL</FieldLabel>
      <input
        type="url"
        className="os-input"
        placeholder="Address"
        value={form.operatorUrl}
        onChange={setField('operatorUrl')}
      />
    </div>
  );
}

